import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Check, HeartStraight } from '@liveauctioneers/hammer-ui-core/icons/v2';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { CompleteAccountModalContext } from '@/redux/modules/modal';
import {
    createSaveSearchKeyBySearchParams,
    deleteSavedSearch,
    getIsSavedSearch,
    submitSaveSearch,
} from '@/redux/modules/saveSearch';
import { DEFAULT_STATUS_FOR_ONLINE_SEARCH } from '@/utils/queryParams';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { getItemModelTitle } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getItemUrl } from '@/utils/urls';
import { toast } from '@liveauctioneers/hammer-ui-core/toast';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRequireLogin } from '@/components/ShouldLogIn/hooks/useRequireLogin';
import { useTheme } from 'styled-components';
import {
    useTrackFollowedPriceResultsSearch,
    useTrackMainItemClickedPriceResults,
} from '@/hooks/kameleoon/tracking/useTrackPriceResultsPage';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';

type Props = {
    className?: string;
    itemId: number;
};

export const PriceResultsItemCTA = ({ className, itemId }: Props) => {
    const verifyLogin = useRequireLogin();
    const { formatMessage } = useIntl();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const {
        breakpoints: { tabletNarrowWidthNum },
    } = useTheme();
    const isMobile = useIsNarrowerThan(tabletNarrowWidthNum + 50);

    const title = useAppSelector((state) => getItemModelTitle(state, itemId));
    const saveSearchParams = useMemo(() => {
        return { keyword: title, status: DEFAULT_STATUS_FOR_ONLINE_SEARCH };
    }, [title]);
    const saveSearchKey = createSaveSearchKeyBySearchParams(saveSearchParams);
    const isSavedSearch = useAppSelector((state) => getIsSavedSearch(state, saveSearchKey));

    const itemPageUrl = getItemUrl(itemId, title);

    const trackFollowedPriceResultsSearch = useTrackFollowedPriceResultsSearch();
    const trackMainItemCardClicked = useTrackMainItemClickedPriceResults();

    const onFollowSearchClick = async () => {
        await verifyLogin({
            modalContext: { id: itemId, type: CompleteAccountModalContext.FollowSearch },
            requireLogin: true,
            runFunction: () => {
                if (isSavedSearch) {
                    dispatch(deleteSavedSearch(saveSearchParams));
                    toast('success', {
                        description: formatMessage({ id: 'searchRemoveAlertToast' }),
                        title: formatMessage({ id: 'toastMessages.titles.success' }),
                    });
                } else {
                    trackFollowedPriceResultsSearch();
                    dispatch(submitSaveSearch(saveSearchParams, true));
                    toast('success', {
                        description: formatMessage({ id: 'searchCreateAlertToast' }),
                        title: formatMessage({ id: 'toastMessages.titles.success' }),
                    });
                }
            },
        });
    };

    const onViewItemDetailsClick = async () => {
        await verifyLogin({
            modalContext: { id: itemId, type: CompleteAccountModalContext.SeeSoldPrice },
            requireLogin: true,
            runFunction: () => {
                trackMainItemCardClicked();
                navigate({ pathname: itemPageUrl });
            },
        });
    };

    return (
        <div className={cn('flex h-full items-end gap-sm xsMax:justify-between', className)}>
            <Button
                className="xsMax:w-full"
                iconLeft={isSavedSearch ? Check : HeartStraight}
                onClick={onFollowSearchClick}
                variant="outlined"
            >
                {isSavedSearch
                    ? formatMessage({ id: 'following' })
                    : formatMessage({
                          id: isMobile ? 'priceResultsPage.followSearchShort' : 'priceResultsPage.followSearch',
                      })}
            </Button>
            <Button
                className="xsMax:w-full"
                onClick={onViewItemDetailsClick}
            >
                <FormattedMessage
                    id={isMobile ? 'priceResultsPage.viewItemDetailsShort' : 'priceResultsPage.viewItemDetails'}
                />
            </Button>
        </div>
    );
};
